import { getPlanEntitlements, planStore } from './planStore';
import { restoreFocusWithoutRing } from './modalAccessibility';
import {
	buildSubscriptionChooserState,
	type BillingCycle,
	type PlanCatalogEntry,
	type SubscriptionChooserContext,
	type SubscriptionChooserState,
	type SubscriptionPlan,
} from './subscriptionChooserState';
import type { Root } from './prototypeTypes';
import type { ClientPlanContext } from './clientPlanContext';

export type { SubscriptionPlan } from './subscriptionChooserState';

export interface SubscriptionChooserInstance {
	update(context: SubscriptionChooserContext): void;
	reset(context: SubscriptionChooserContext): void;
	destroy(): void;
}

export interface SubscriptionPurchaseIntent {
	instanceId: string;
	source: SubscriptionChooserContext['source'];
	billingPlan: 'free' | 'lite' | 'pro' | 'max';
	targetPlan: SubscriptionPlan;
	billingCycle: BillingCycle;
	currentBillingCycle: BillingCycle | null;
	providerSubscriptionId: string | null;
	noImmediateAccessIncrease: boolean;
}

/** Builds the authenticated context only from the validated session state and a current subscription response. */
export function subscriptionChooserContextFromState(
	state: { currentPlan: string; planContext: ClientPlanContext | null; runtime?: { crossCycleUpgradeEnabled?: boolean } },
	input: {
		source: SubscriptionChooserContext['source'];
		requiredPlan?: SubscriptionPlan;
		subscription?: {
			providerSubscriptionId?: string;
			plan: string;
			billingCycle: string;
			currentPeriodEnd: string | null;
			cancelAtPeriodEnd: boolean;
		} | null;
	},
): SubscriptionChooserContext | null {
	if (state.currentPlan === 'visitor') return { kind: 'visitor', source: input.source, requiredPlan: input.requiredPlan };
	if (!state.planContext) return null;
	const planContext = {
		...state.planContext,
		// An authenticated session parser never returns visitor here, but keep
		// the controller boundary explicit rather than allowing that legacy type.
		effectiveAccessPlan: state.planContext.effectiveAccessPlan === 'visitor' ? state.planContext.billingPlan : state.planContext.effectiveAccessPlan,
	};
	if (planContext.billingPlan === 'free') {
		return {
			kind: 'authenticated',
			source: input.source,
			requiredPlan: input.requiredPlan,
			crossCycleUpgrade: state.runtime?.crossCycleUpgradeEnabled === true,
			planContext,
			billingSubscription: { providerSubscriptionId: null, billingCycle: null, currentPeriodEnd: null, cancelAtPeriodEnd: false },
		};
	}
	const subscription = input.subscription;
	const billingCycle = subscription?.billingCycle;
	const validCycle = billingCycle === 'monthly' || billingCycle === 'quarterly' || billingCycle === 'annual';
	const samePaidPlan = subscription?.plan === planContext.billingPlan;
	return {
		kind: 'authenticated',
		source: input.source,
		requiredPlan: input.requiredPlan,
		crossCycleUpgrade: state.runtime?.crossCycleUpgradeEnabled === true,
		planContext,
		// A stale or absent subscription never gets guessed from state.currentPlan.
		billingSubscription: {
			providerSubscriptionId: samePaidPlan && typeof subscription.providerSubscriptionId === 'string'
				? subscription.providerSubscriptionId
				: null,
			billingCycle: validCycle && samePaidPlan ? billingCycle : null,
			currentPeriodEnd: samePaidPlan ? subscription.currentPeriodEnd : null,
			cancelAtPeriodEnd: samePaidPlan ? subscription.cancelAtPeriodEnd : false,
		},
	};
}

type MountedChooser = SubscriptionChooserInstance & {
	panel: HTMLElement;
	context: SubscriptionChooserContext;
	selectedPlan: SubscriptionPlan;
	billingCycle: BillingCycle | null;
	billingCycleTouched: boolean;
	mediaQuery: MediaQueryList | null;
	onViewportChange: (() => void) | null;
	onScroll: (() => void) | null;
	onPlanTabKeydown: ((event: KeyboardEvent) => void) | null;
	revealGeneration: number;
	revealTimer: ReturnType<typeof setTimeout> | null;
	onRevealStart: ((event: AnimationEvent) => void) | null;
	onRevealEnd: ((event: AnimationEvent) => void) | null;
	fallbackList: boolean;
};

const mounted = new WeakMap<HTMLElement, MountedChooser>();
const revealGenerations = new WeakMap<HTMLElement, number>();
const paidPlans: SubscriptionPlan[] = ['lite', 'pro', 'max'];

/**
 * Mounts the single chooser implementation into any surface. Mutable state is
 * intentionally per panel, never a module-level billing-cycle singleton.
 */
export function mountSubscriptionChooser(root: Root, input: { prefix: string; context: SubscriptionChooserContext }): SubscriptionChooserInstance | null {
	const panel = root.querySelector<HTMLElement>(`[data-prefix="${input.prefix}"]`);
	if (!panel) return null;
	mounted.get(panel)?.destroy();
	const catalog = catalogEntries();
	const initial = buildSubscriptionChooserState({ context: input.context, plans: catalog });
	const instance: MountedChooser = {
		panel,
		context: input.context,
		selectedPlan: initial.selectedPlan,
		billingCycle: initial.billingCycle,
		billingCycleTouched: false,
		mediaQuery: typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 760px)') : null,
		onViewportChange: null,
		onScroll: null,
		onPlanTabKeydown: null,
		revealGeneration: revealGenerations.get(panel) ?? 0,
		revealTimer: null,
		onRevealStart: null,
		onRevealEnd: null,
		fallbackList: typeof window === 'undefined' || typeof window.matchMedia !== 'function',
		update(context) {
			this.context = context;
			if (!this.billingCycleTouched) {
				this.billingCycle = buildSubscriptionChooserState({ context, plans: catalogEntries(), selectedPlan: this.selectedPlan }).billingCycle;
			}
			paint(this);
		},
		reset(context) {
			this.context = context;
			this.billingCycleTouched = false;
			const state = buildSubscriptionChooserState({ context, plans: catalogEntries() });
			this.selectedPlan = state.selectedPlan;
			this.billingCycle = state.billingCycle;
			paint(this);
		},
		destroy() {
			invalidateInitialReveal(this);
			if (this.mediaQuery && this.onViewportChange) this.mediaQuery.removeEventListener('change', this.onViewportChange);
			const tierTrack = this.panel.querySelector<HTMLElement>('.sp-tiers');
			if (tierTrack && this.onScroll) tierTrack.removeEventListener('scroll', this.onScroll);
			const planTabs = this.panel.querySelector<HTMLElement>('.sp-plan-tabs');
			if (planTabs && this.onPlanTabKeydown) planTabs.removeEventListener('keydown', this.onPlanTabKeydown);
			mounted.delete(this.panel);
		},
	};
	instance.onViewportChange = () => syncViewport(instance);
	instance.mediaQuery?.addEventListener('change', instance.onViewportChange);
	const tierTrack = panel.querySelector<HTMLElement>('.sp-tiers');
	instance.onScroll = () => syncSelectedPlanFromScroll(instance);
	tierTrack?.addEventListener('scroll', instance.onScroll, { passive: true });
	const planTabs = panel.querySelector<HTMLElement>('.sp-plan-tabs');
	instance.onPlanTabKeydown = (event) => navigatePlanTabs(instance, event);
	planTabs?.addEventListener('keydown', instance.onPlanTabKeydown);
	mounted.set(panel, instance);
	paint(instance);
	return instance;
}

export function updateSubscriptionChooser(root: Root, prefix: string, context: SubscriptionChooserContext): SubscriptionChooserInstance | null {
	const panel = root.querySelector<HTMLElement>(`[data-prefix="${prefix}"]`);
	const instance = panel ? mounted.get(panel) : undefined;
	if (instance) {
		instance.update(context);
		return instance;
	}
	return mountSubscriptionChooser(root, { prefix, context });
}

/** Hide a chooser until its opening subscription read has supplied trustworthy facts. */
export function setSubscriptionChooserLoading(root: Root, prefix: string): void {
	const panel = root.querySelector<HTMLElement>(`[data-prefix="${prefix}"]`);
	if (!panel) return;
	const instance = mounted.get(panel);
	if (instance) {
		clearInitialReveal(instance);
		instance.revealGeneration = nextRevealGeneration(panel);
	} else {
		nextRevealGeneration(panel);
	}
	panel.hidden = false;
	panel.setAttribute('aria-busy', 'true');
	panel.setAttribute('aria-hidden', 'true');
	panel.setAttribute('inert', '');
	panel.dataset.loadState = 'loading';
	delete panel.dataset.activeCycle;
	delete panel.dataset.selectedPlan;
	delete panel.dataset.reveal;
	panel.querySelector<HTMLElement>('.sp-tiers')?.setAttribute('data-active-plan', '');
	panel.querySelectorAll<HTMLElement>('.sp-tier').forEach((card) => {
		card.classList.remove('selected', 'sp-current', 'sp-disabled', 'sp-upgrade', 'sp-featured');
		card.setAttribute('aria-hidden', 'false');
		card.removeAttribute('inert');
	});
	panel.classList.remove('sp-mobile-chooser', 'sp-fallback-list');
	const surface = panel.closest<HTMLElement>('[data-subscription-surface]');
	if (surface) surface.dataset.subscriptionState = 'loading';
	const cycles = panel.querySelector<HTMLElement>('.sp-billing-tabs');
	if (cycles) cycles.dataset.activeCycle = '';
	panel.querySelectorAll<HTMLButtonElement>('.sp-tab, .sp-plan-tab, [data-tier-btn]').forEach((button) => {
		button.disabled = true;
		button.setAttribute('aria-disabled', 'true');
		button.setAttribute('aria-selected', 'false');
		button.classList.remove('active');
	});
	setChooserStatus(panel, 'Loading your current subscription…', false);
}

/** Keep an unsafe paid chooser hidden and offer an explicit fresh read. */
export function setSubscriptionChooserError(root: Root, prefix: string): void {
	setSubscriptionChooserLoading(root, prefix);
	const panel = root.querySelector<HTMLElement>(`[data-prefix="${prefix}"]`);
	if (!panel) return;
	panel.setAttribute('aria-busy', 'false');
	panel.dataset.loadState = 'error';
	const surface = panel.closest<HTMLElement>('[data-subscription-surface]');
	if (surface) surface.dataset.subscriptionState = 'error';
	setChooserStatus(panel, 'We could not load your current subscription. Please try again.', true);
}

/** The signed-in session is valid, but production has no saleable catalog.
 * Keep purchase controls inert without incorrectly blaming subscription reads. */
export function setSubscriptionChooserPurchasesUnavailable(root: Root, prefix: string): void {
	setSubscriptionChooserLoading(root, prefix);
	const panel = root.querySelector<HTMLElement>(`[data-prefix="${prefix}"]`);
	if (!panel) return;
	panel.setAttribute('aria-busy', 'false');
	panel.dataset.loadState = 'purchases_unavailable';
	const surface = panel.closest<HTMLElement>('[data-subscription-surface]');
	if (surface) surface.dataset.subscriptionState = 'purchases_unavailable';
	setChooserStatus(panel, 'Plan purchases are not open yet.', false);
}

export function setSubscriptionChooserBillingCycle(root: Root, source: HTMLElement | null, cycle: BillingCycle): void {
	const instance = instanceFor(root, source);
	if (!instance || !isChooserSelectable(instance)) return;
	finishInitialRevealForInteraction(instance);
	const state = stateFor(instance);
	if (state.cycles.find((item) => item.cycle === cycle)?.disabled) return;
	instance.billingCycle = cycle;
	instance.billingCycleTouched = true;
	paint(instance);
}

export function selectSubscriptionChooserPlan(root: Root, source: HTMLElement | null): void {
	const instance = instanceFor(root, source);
	const plan = source?.dataset.plan as SubscriptionPlan | undefined;
	if (!instance || !isChooserSelectable(instance) || !plan || !paidPlans.includes(plan)) return;
	finishInitialRevealForInteraction(instance);
	instance.selectedPlan = plan;
	paint(instance, true, true);
}

export function readSubscriptionPurchaseIntent(root: Root, source: HTMLElement | null, targetPlan: SubscriptionPlan): SubscriptionPurchaseIntent | null {
	const instance = instanceFor(root, source);
	if (!instance || !paidPlans.includes(targetPlan)) return null;
	if (!isChooserReadyForInteraction(instance)) return null;
	const state = stateFor(instance);
	const card = state.cards.find((item) => item.plan === targetPlan);
	if (!card || card.disabled || (card.action !== 'signup' && card.action !== 'checkout' && card.action !== 'upgrade')) return null;
	if (
		instance.context.kind === 'authenticated'
		&& instance.context.planContext.billingPlan !== 'free'
		&& (!instance.context.billingSubscription.billingCycle || !instance.context.billingSubscription.providerSubscriptionId)
	) return null;
	return {
		instanceId: instance.panel.dataset.prefix ?? '',
		source: instance.context.source,
		billingPlan: instance.context.kind === 'authenticated' ? instance.context.planContext.billingPlan : 'free',
		targetPlan,
		billingCycle: state.billingCycle,
		currentBillingCycle: instance.context.kind === 'authenticated' ? instance.context.billingSubscription.billingCycle : null,
		providerSubscriptionId: instance.context.kind === 'authenticated'
			? instance.context.billingSubscription.providerSubscriptionId ?? null
			: null,
		noImmediateAccessIncrease: card.noImmediateAccessIncrease,
	};
}

/** A public catalog may send a paid member to the paywall for a strictly
 * higher tier, but must never guess that member's subscription cycle. */
export function shouldOpenPaywallForPublicUpgrade(root: Root, source: HTMLElement | null, targetPlan: SubscriptionPlan): boolean {
	const instance = instanceFor(root, source);
	if (!instance || instance.context.kind !== 'authenticated' || !paidPlans.includes(targetPlan)) return false;
	if (instance.context.source !== 'pricing' && instance.context.source !== 'ai-gate' && instance.context.source !== 'github-gate') return false;
	if (instance.context.billingSubscription.billingCycle || instance.context.billingSubscription.providerSubscriptionId) return false;
	if (instance.context.planContext.billingPlan === 'free') return false;
	return paidPlans.indexOf(targetPlan) > paidPlans.indexOf(instance.context.planContext.billingPlan as SubscriptionPlan);
}

function instanceFor(root: Root, source: HTMLElement | null): MountedChooser | undefined {
	const panel = source?.closest<HTMLElement>('[data-prefix]') ?? root.querySelector<HTMLElement>('[data-prefix="pw"]');
	return panel ? mounted.get(panel) : undefined;
}

function isChooserReadyForInteraction(instance: MountedChooser): boolean {
	return !instance.panel.dataset.loadState
		&& !instance.panel.hasAttribute('inert')
		&& instance.panel.getAttribute('aria-hidden') !== 'true';
}

function isChooserSelectable(instance: MountedChooser): boolean {
	return instance.panel.dataset.loadState !== 'loading' && instance.panel.dataset.loadState !== 'error';
}

function stateFor(instance: MountedChooser): SubscriptionChooserState {
	return buildSubscriptionChooserState({
		context: instance.context,
		plans: catalogEntries(),
		selectedPlan: instance.selectedPlan,
		billingCycle: instance.billingCycle ?? undefined,
	});
}

function catalogEntries(): PlanCatalogEntry[] {
	const catalog = planStore.getAll();
	if (!catalog) return [];
	return paidPlans.map((code) => {
		const plan = catalog[code];
		return {
			code,
			monthlyCents: Number(plan?.monthlyCents ?? 0),
			quarterlyCents: Number(plan?.quarterlyCents ?? 0),
			annualCents: Number(plan?.annualCents ?? 0),
		};
	});
}

function paint(instance: MountedChooser, focusSelected = false, suppressFocusRing = false): void {
	const shouldReveal = prepareChooser(instance);
	const state = stateFor(instance);
	instance.selectedPlan = state.selectedPlan;
	instance.billingCycle = state.billingCycle;
	instance.panel.dataset.selectedPlan = state.selectedPlan;
	instance.panel.dataset.activeCycle = state.billingCycle;
	instance.panel.dataset.pricingReady = String(catalogEntries().some((entry) => entry.monthlyCents > 0 || entry.quarterlyCents > 0 || entry.annualCents > 0));
	paintCycles(instance, state);
	paintPrices(instance, state.billingCycle);
	paintCards(instance, state);
	syncViewport(instance);
	if (focusSelected && isMobile(instance)) {
		const planTab = instance.panel.querySelector<HTMLElement>(`#${instance.panel.dataset.prefix}PlanTab${capitalize(state.selectedPlan)}`);
		// Pointer picks re-park focus on the repainted tab without a halo;
		// arrow-key navigation keeps the ring as its focus indicator.
		if (suppressFocusRing) restoreFocusWithoutRing(planTab);
		else planTab?.focus();
	}
	if (shouldReveal) armInitialReveal(instance);
}

function paintCycles(instance: MountedChooser, state: SubscriptionChooserState): void {
	const container = instance.panel.querySelector<HTMLElement>('.sp-billing-tabs');
	if (!container) return;
	container.dataset.activeCycle = state.billingCycle;
	for (const item of state.cycles) {
		const button = container.querySelector<HTMLButtonElement>(`[data-cycle="${item.cycle}"]`);
		if (!button) continue;
		const selected = item.cycle === state.billingCycle;
		button.disabled = false;
		button.classList.toggle('active', selected);
		button.setAttribute('aria-disabled', String(item.disabled));
		button.setAttribute('aria-selected', String(selected));
		if (item.reason) {
			button.setAttribute('aria-label', `${capitalize(item.cycle)} billing unavailable: ${item.reason}`);
			button.title = item.reason;
		} else {
			button.removeAttribute('aria-label');
			button.removeAttribute('title');
		}
	}
	const pricingStatus = instance.panel.closest<HTMLElement>('[data-subscription-surface]')?.querySelector<HTMLElement>('.sp-pricing-status');
	if (pricingStatus) pricingStatus.hidden = catalogEntries().some((entry) => entry.monthlyCents > 0 || entry.quarterlyCents > 0 || entry.annualCents > 0);
}

function prepareChooser(instance: MountedChooser): boolean {
	const panel = instance.panel;
	const surface = panel.closest<HTMLElement>('[data-subscription-surface]');
	const shouldReveal = panel.dataset.loadState === 'loading';
	panel.hidden = false;
	if (!shouldReveal) {
		if (panel.dataset.loadState !== 'revealing' || instance.context.kind === 'visitor') unlockChooser(panel);
		return false;
	}
	panel.dataset.loadState = 'revealing';
	if (surface) surface.dataset.subscriptionState = 'revealing';
	const retry = surface?.querySelector<HTMLButtonElement>('[data-proto-click="retryPaywallSubscription()"]');
	if (retry) retry.hidden = true;
	return true;
}

function armInitialReveal(instance: MountedChooser): void {
	clearInitialReveal(instance);
	instance.panel.dataset.reveal = 'initial';
	const generation = instance.revealGeneration;
	if (prefersReducedMotion()) {
		completeInitialReveal(instance, generation);
		return;
	}
	instance.onRevealStart = (event) => {
		if (!isRevealEventForPanel(instance, event)) return;
		armRevealWatchdog(instance, generation, 270);
	};
	instance.onRevealEnd = (event) => {
		if (!isRevealEventForPanel(instance, event)) return;
		completeInitialReveal(instance, generation);
	};
	instance.panel.addEventListener('animationstart', instance.onRevealStart);
	instance.panel.addEventListener('animationend', instance.onRevealEnd);
	armRevealWatchdog(instance, generation, 500);
}

function armRevealWatchdog(instance: MountedChooser, generation: number, delay: number): void {
	if (instance.revealTimer !== null) clearTimeout(instance.revealTimer);
	instance.revealTimer = setTimeout(() => completeInitialReveal(instance, generation), delay);
}

function completeInitialReveal(instance: MountedChooser, generation: number): void {
	if (!canCompleteInitialReveal(instance, generation)) {
		if (instance.revealGeneration === generation && revealGenerations.get(instance.panel) === generation) clearInitialReveal(instance);
		return;
	}
	clearInitialReveal(instance);
	unlockChooser(instance.panel);
}

function canCompleteInitialReveal(instance: MountedChooser, generation: number): boolean {
	const surface = instance.panel.closest<HTMLElement>('[data-subscription-surface]');
	if (instance.revealGeneration !== generation || revealGenerations.get(instance.panel) !== generation) return false;
	if (surface?.dataset.subscriptionState !== 'revealing') return false;
	if (instance.panel.dataset.prefix !== 'pw') return true;
	return instance.panel.ownerDocument.querySelector<HTMLElement>('#paywallModal')?.classList.contains('active') === true;
}

function isRevealEventForPanel(instance: MountedChooser, event: AnimationEvent): boolean {
	return event.target === instance.panel && event.animationName === 'sp-chooser-reveal';
}

function unlockChooser(panel: HTMLElement): void {
	panel.hidden = false;
	panel.removeAttribute('aria-busy');
	panel.removeAttribute('aria-hidden');
	panel.removeAttribute('inert');
	delete panel.dataset.loadState;
	const surface = panel.closest<HTMLElement>('[data-subscription-surface]');
	if (surface) surface.dataset.subscriptionState = 'ready';
	const retry = surface?.querySelector<HTMLButtonElement>('[data-proto-click="retryPaywallSubscription()"]');
	if (retry) retry.hidden = true;
}

function clearInitialReveal(instance: MountedChooser): void {
	if (instance.revealTimer !== null) clearTimeout(instance.revealTimer);
	instance.revealTimer = null;
	if (instance.onRevealStart) instance.panel.removeEventListener('animationstart', instance.onRevealStart);
	if (instance.onRevealEnd) instance.panel.removeEventListener('animationend', instance.onRevealEnd);
	instance.onRevealStart = null;
	instance.onRevealEnd = null;
	delete instance.panel.dataset.reveal;
}

function invalidateInitialReveal(instance: MountedChooser): void {
	clearInitialReveal(instance);
	instance.revealGeneration = nextRevealGeneration(instance.panel);
}

function finishInitialRevealForInteraction(instance: MountedChooser): void {
	const generation = instance.revealGeneration;
	if (canCompleteInitialReveal(instance, generation)) {
		clearInitialReveal(instance);
		unlockChooser(instance.panel);
		return;
	}
	invalidateInitialReveal(instance);
}

function nextRevealGeneration(panel: HTMLElement): number {
	const generation = (revealGenerations.get(panel) ?? 0) + 1;
	revealGenerations.set(panel, generation);
	return generation;
}

function prefersReducedMotion(): boolean {
	return typeof window !== 'undefined'
		&& typeof window.matchMedia === 'function'
		&& window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function setChooserStatus(panel: HTMLElement, message: string, retryVisible: boolean): void {
	const prefix = panel.dataset.prefix ?? '';
	const surface = panel.closest<HTMLElement>('[data-subscription-surface]');
	const status = surface?.querySelector<HTMLElement>(`#${prefix}PricingStatus`);
	if (status) {
		status.textContent = message;
		status.hidden = false;
	}
	const retry = surface?.querySelector<HTMLButtonElement>(`#${prefix}PricingRetry`);
	if (retry) {
		retry.hidden = !retryVisible;
		retry.disabled = !retryVisible;
		retry.setAttribute('aria-disabled', String(!retryVisible));
	}
}

function paintPrices(instance: MountedChooser, cycle: BillingCycle): void {
	const prefix = instance.panel.dataset.prefix ?? '';
	for (const plan of paidPlans) {
		const entitlement = getPlanEntitlements(plan);
		const amount = cycle === 'monthly' ? entitlement.monthlyCents : cycle === 'quarterly' ? entitlement.quarterlyCents : entitlement.annualCents;
		const months = cycle === 'annual' ? 12 : cycle === 'quarterly' ? 3 : 1;
		setText(instance.panel, `#${prefix}Orig${capitalize(plan)}`, amount > 0 && entitlement.monthlyCents > 0 && cycle !== 'monthly' ? `$${(entitlement.monthlyCents / 100).toFixed(2)}/mo` : '');
		setText(instance.panel, `#${prefix}Price${capitalize(plan)}`, amount > 0 ? (amount / months / 100).toFixed(2) : '—');
		setText(instance.panel, `#${prefix}Bill${capitalize(plan)}`, amount > 0 ? `$${(amount / 100).toFixed(2)} billed ${cycle === 'monthly' ? 'monthly' : cycle === 'quarterly' ? 'every 3 months' : 'yearly'}` : 'Not available for this cycle');
		setText(instance.panel, `#${prefix}Save${capitalize(plan)}`, savingLabel(entitlement.monthlyCents, amount, months));
		const aiFeature = instance.panel.querySelector<HTMLElement>(`#${prefix}Ai${capitalize(plan)}Feature`);
		if (aiFeature && Number.isFinite(entitlement.privateChaptersPerMonth)) aiFeature.textContent = `AI Create: ${entitlement.privateChaptersPerMonth} private chapters/mo`;
		const githubFeature = instance.panel.querySelector<HTMLElement>(`#${prefix}Github${capitalize(plan)}Feature`);
		if (githubFeature) githubFeature.hidden = entitlement.githubDeepDive !== true;
	}
}

function paintCards(instance: MountedChooser, state: SubscriptionChooserState): void {
	for (const cardState of state.cards) {
		const card = instance.panel.querySelector<HTMLElement>(`[data-tier="${cardState.plan}"]`);
		const button = card?.querySelector<HTMLButtonElement>(`[data-tier-btn="${cardState.plan}"]`);
		if (!card || !button) continue;
		const selected = cardState.plan === state.selectedPlan;
		card.classList.toggle('selected', selected);
		card.classList.toggle('sp-current', cardState.action === 'current');
		card.classList.toggle('sp-disabled', cardState.disabled);
		card.classList.toggle('sp-upgrade', cardState.action === 'checkout' || cardState.action === 'upgrade' || cardState.action === 'signup');
		card.classList.toggle('sp-featured', cardState.plan === 'pro' && cardState.action !== 'current');
		button.className = `sp-btn ${cardState.disabled ? 'sp-btn-disabled' : cardState.action === 'current' ? 'sp-btn-current' : 'sp-btn-upgrade'}`;
		button.disabled = cardState.action === 'current';
		button.setAttribute('aria-disabled', String(cardState.disabled));
		button.removeAttribute('data-proto-click');
		button.textContent = buttonLabel(cardState.plan, cardState.action, state.billingCycle, instance.context);
		if (cardState.disabledReason) {
			button.setAttribute('aria-label', `${button.textContent}: ${cardState.disabledReason}`);
			button.title = cardState.disabledReason;
		} else {
			button.removeAttribute('aria-label');
			button.removeAttribute('title');
		}
		if (!cardState.disabled) button.setAttribute('data-proto-click', `chooseSubscriptionPlan('${cardState.plan}')`);
	}
}

export function setMountedSubscriptionChooserCapability(root: Root, enabled: boolean): void {
	for (const panel of root.querySelectorAll<HTMLElement>('[data-prefix]')) {
		const instance = mounted.get(panel);
		if (!instance || instance.context.kind !== 'authenticated') continue;
		instance.update({ ...instance.context, crossCycleUpgrade: enabled });
	}
}

function syncViewport(instance: MountedChooser): void {
	if (instance.fallbackList) {
		instance.panel.classList.add('sp-fallback-list');
		instance.panel.classList.remove('sp-mobile-chooser');
		for (const plan of paidPlans) {
			const card = instance.panel.querySelector<HTMLElement>(`[data-tier="${plan}"]`);
			const tab = instance.panel.querySelector<HTMLButtonElement>(`[data-plan="${plan}"]`);
			card?.setAttribute('aria-hidden', 'false');
			card?.removeAttribute('inert');
			tab?.setAttribute('aria-disabled', 'true');
			tab && (tab.tabIndex = -1);
		}
		return;
	}
	const mobile = isMobile(instance);
	instance.panel.classList.remove('sp-fallback-list');
	for (const plan of paidPlans) {
		const card = instance.panel.querySelector<HTMLElement>(`[data-tier="${plan}"]`);
		const tab = instance.panel.querySelector<HTMLButtonElement>(`[data-plan="${plan}"]`);
		if (!card || !tab) continue;
		const selected = plan === instance.selectedPlan;
		// Loading temporarily disables every tab to prevent stale interactions.
		// Once a real chooser is painted, tabs remain browsable even when their
		// card CTA is unavailable; purchaseability stays on the card button.
		tab.disabled = false;
		tab.setAttribute('aria-disabled', 'false');
		tab.classList.toggle('active', selected);
		tab.setAttribute('aria-selected', String(selected));
		tab.tabIndex = selected ? 0 : -1;
		card.setAttribute('aria-hidden', String(mobile && !selected));
		card.toggleAttribute('inert', mobile && !selected);
	}
	instance.panel.classList.toggle('sp-mobile-chooser', mobile);
}

function syncSelectedPlanFromScroll(instance: MountedChooser): void {
	if (!isMobile(instance)) return;
	try {
		const track = instance.panel.querySelector<HTMLElement>('.sp-tiers');
		if (!track || track.clientWidth === 0) return;
		const candidate = paidPlans.map((plan) => {
			const card = track.querySelector<HTMLElement>(`[data-tier="${plan}"]`);
			if (!card) return { plan, ratio: 0 };
			const rect = card.getBoundingClientRect();
			const trackRect = track.getBoundingClientRect();
			const visible = Math.max(0, Math.min(rect.right, trackRect.right) - Math.max(rect.left, trackRect.left));
			return { plan, ratio: visible / Math.max(1, rect.width) };
		}).sort((a, b) => b.ratio - a.ratio)[0];
		if (candidate?.ratio >= 0.6 && candidate.plan !== instance.selectedPlan) {
			instance.selectedPlan = candidate.plan;
			syncViewport(instance);
		}
	} catch {
		enableFallbackList(instance);
	}
}

function navigatePlanTabs(instance: MountedChooser, event: KeyboardEvent): void {
	if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
	const selectablePlans = paidPlans;
	if (!selectablePlans.length) return;
	const current = selectablePlans.indexOf(instance.selectedPlan);
	const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? selectablePlans.length - 1 : (Math.max(0, current) + (event.key === 'ArrowRight' ? 1 : -1) + selectablePlans.length) % selectablePlans.length;
	event.preventDefault();
	instance.selectedPlan = selectablePlans[nextIndex];
	paint(instance, true);
}

function enableFallbackList(instance: MountedChooser): void {
	instance.fallbackList = true;
	instance.panel.querySelector<HTMLElement>('.sp-tiers')?.removeEventListener('scroll', instance.onScroll ?? (() => {}));
	instance.panel.querySelector<HTMLElement>('.sp-plan-tabs')?.removeEventListener('keydown', instance.onPlanTabKeydown ?? (() => {}));
	syncViewport(instance);
}

function isMobile(instance: MountedChooser): boolean {
	return instance.mediaQuery?.matches === true;
}

function buttonLabel(
	plan: SubscriptionChooserState['cards'][number]['plan'],
	action: SubscriptionChooserState['cards'][number]['action'],
	cycle: BillingCycle,
	context: SubscriptionChooserContext,
): string {
	const name = capitalize(plan);
	if (action === 'signup') return `Continue with ${name}`;
	if (action === 'checkout') return `Choose ${name}`;
	if (action === 'upgrade') {
		const samePlanCycleChange = context.kind === 'authenticated'
			&& context.planContext.billingPlan === plan
			&& context.billingSubscription.billingCycle !== cycle;
		return samePlanCycleChange ? `Switch to ${capitalize(cycle)} ${name}` : `Upgrade to ${name}`;
	}
	if (action === 'current') return 'Current plan';
	if (action === 'cycle_preview_only') return 'Current cycle only';
	return context.requiredPlan && paidPlans.indexOf(plan) < paidPlans.indexOf(context.requiredPlan) ? `${capitalize(context.requiredPlan)} required` : 'Unavailable';
}

function savingLabel(monthlyCents: number, priceCents: number, months: number): string {
	if (months === 1 || monthlyCents <= 0 || priceCents <= 0) return '';
	const saved = monthlyCents * months - priceCents;
	return saved > 0 ? `Save $${(saved / 100).toFixed(0)}` : '';
}

function setText(parent: HTMLElement, selector: string, value: string): void {
	const element = parent.querySelector<HTMLElement>(selector);
	if (element) element.textContent = value;
}

function capitalize(value: string): string {
	return value.charAt(0).toUpperCase() + value.slice(1);
}
